// Real portal shell using ported Nav + Footer + ViewingAsBanner from
// components/shell.tsx. Wraps every authenticated portal route.

import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';

import { CleanSidebar, Footer, ImpersonationBanner, Nav, StaleBanner, ViewingAsBanner } from '../../components/shell';
import { useAuth } from '../../lib/auth';
import { usePreferences } from '../../lib/preferences';

// StaleBanner shows when the snapshot is older than the SLA. Wire to a
// real signal when we surface snapshot health; off for now.
const SHOW_STALE = false;
const STALE_AS_OF = '—';

export default function PortalLayout() {
  const { user, signOut } = useAuth();
  const { appearance } = usePreferences();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const impersonatingClientId = user?.role === 'manager' ? params.get('client_id') : null;

  const client = { shortName: user?.client_legal_name || user?.display_name || '—' };
  const handleSignOut = async () => {
    await signOut();
    navigate('/sign-in', { replace: true });
  };

  // Clean mode: professional SaaS shell — fixed left sidebar + a content
  // column pinned to the viewport so pages fit one screen (panels scroll
  // internally). Editorial mode keeps the original top-nav shell below.
  if (appearance === 'clean') {
    return (
      <div className="clean-app">
        <CleanSidebar client={client} onSignOut={handleSignOut} role={user?.role} />
        <div className="clean-content">
          <ImpersonationBanner />
          {impersonatingClientId && <ViewingAsBanner clientName={`client ${impersonatingClientId.slice(0, 8)}…`} />}
          <main className="clean-main view-fade">
            <Outlet />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)', color: 'var(--ink)', display: 'flex', flexDirection: 'column' }}>
      <Nav client={client} onSignOut={handleSignOut} role={user?.role} />
      <ImpersonationBanner />
      {impersonatingClientId && <ViewingAsBanner clientName={`client ${impersonatingClientId.slice(0, 8)}…`} />}
      {SHOW_STALE && <StaleBanner asOf={STALE_AS_OF} />}
      <main className="view-fade" style={{ flex: 1, maxWidth: 'var(--max)', margin: '0 auto', width: '100%', padding: 'var(--pad-y) var(--pad-x)' }}>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
